import {defaultLocale, Locale, locales} from './locales';
import {getLocalStorageItem, setLocalStorageItem} from '@/lib/storageUtils';

export const USER_LOCALE_STORAGE_KEY = 'braiv_user_locale';
export const LEGACY_LOCALE_STORAGE_KEY = 'locale';

const VALID_LOCALES = new Set<string>(locales);

/**
 * Maps marketing-site locale tags (e.g. zh-CN, pt-BR) to supported app locales.
 */
export function normalizeMarketingLocale(raw: string | null | undefined): Locale | null {
    if (!raw) return null;

    const trimmed = raw.trim();
    if (!trimmed) return null;

    const lower = trimmed.toLowerCase();

    if (VALID_LOCALES.has(lower)) {
        return lower as Locale;
    }

    const primary = lower.split('-')[0];
    if (VALID_LOCALES.has(primary)) {
        return primary as Locale;
    }

    return null;
}

export function parseLocaleFromSearchParams(params: URLSearchParams): string | null {
    return params.get('locale') || params.get('lang');
}

export function persistUserLocale(locale: Locale): void {
    setLocalStorageItem(USER_LOCALE_STORAGE_KEY, locale);
    setLocalStorageItem(LEGACY_LOCALE_STORAGE_KEY, locale);
}

function readStoredUserLocale(): Locale | null {
    const stored =
        getLocalStorageItem(USER_LOCALE_STORAGE_KEY) ||
        getLocalStorageItem(LEGACY_LOCALE_STORAGE_KEY);
    return normalizeMarketingLocale(stored);
}

function readBrowserLocale(): Locale | null {
    if (typeof navigator === 'undefined') return null;

    const candidates = [
        navigator.language,
        ...(navigator.languages || []),
    ];

    for (const candidate of candidates) {
        const normalized = normalizeMarketingLocale(candidate);
        if (normalized) return normalized;
    }

    return null;
}

/**
 * Resolves the visitor locale synchronously on the client.
 * Priority: URL ?locale / ?lang → localStorage → browser language → English.
 */
export function resolveVisitorLocale(): Locale {
    if (typeof window === 'undefined') return defaultLocale;

    const params = new URLSearchParams(window.location.search);
    const fromUrl = normalizeMarketingLocale(parseLocaleFromSearchParams(params));
    if (fromUrl) {
        persistUserLocale(fromUrl);
        return fromUrl;
    }

    const fromStorage = readStoredUserLocale();
    if (fromStorage) return fromStorage;

    const fromBrowser = readBrowserLocale();
    if (fromBrowser) return fromBrowser;

    return defaultLocale;
}
